import { ClusterOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import type {
  DeptRow,
  DeptSaveRequest,
  DeptType,
  LocationRow,
  LocationSaveRequest,
  LocationZoneType,
  OrgRow,
  SpaceNodeType,
  WarehouseKind,
  WarehouseNature,
  WarehouseRow,
  WarehouseSaveRequest
} from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { RegionAddressFields } from '@hq/ui-kit';
import type { HqApi } from '@hq/api-sdk';

const WH_KIND_META: Record<WarehouseKind, { text: string; color: string }> = {
  INNER_MAIN: { text: '院内中心库', color: 'blue' },
  INNER_SUB: { text: '科室二级库', color: 'cyan' },
  OUTER_ZERO: { text: '外部零库存仓', color: 'gold' },
  SUPPLIER: { text: '供应商库', color: 'purple' }
};

const DEPT_TYPE_META: Record<DeptType, string> = {
  CLINICAL: '临床科室',
  ADMIN: '行政科室',
  LOGISTICS: '后勤',
  NURSING_UNIT: '护理单元',
  OPD_EMERG: '门急诊',
  LANDMARK: '地标'
};

const ZONE_META: Record<LocationZoneType, { text: string; color: string }> = {
  STORAGE: { text: '存储', color: 'blue' },
  PICK: { text: '拣选', color: 'cyan' },
  RECEIVE: { text: '收货', color: 'green' },
  RETURN: { text: '退货暂存', color: 'orange' },
  SCRAP: { text: '报废区', color: 'red' }
};

interface WarehouseFormValues {
  whCode: string;
  whName: string;
  whKind: WarehouseKind;
  whNature: WarehouseNature;
  deptId?: string;
  /** 责任供应商：OUTER_ZERO / SUPPLIER 仓必填（供应商门户可见范围基准）。 */
  supplierId?: string;
  /** 级联显示值，落库走 provinceCode/cityCode/districtCode。 */
  regionCodes?: string[];
  provinceCode?: string;
  cityCode?: string;
  districtCode?: string;
  address?: string;
  lng?: number | null;
  lat?: number | null;
  contactPhone?: string;
  allowNegative: boolean;
  status: 'ACTIVE' | 'LOCKED';
  remark?: string;
}

interface DeptFormValues {
  deptCode: string;
  deptName: string;
  deptType: DeptType;
  spaceNodeType: SpaceNodeType;
  parentId?: string;
  acceptDelivery: boolean;
  canCostBear: boolean;
  isSubStock: boolean;
  allowSelfApply: boolean;
  buildingId?: string;
  floorId?: string;
  staffCount: number;
  bedCount: number;
  status: 'ACTIVE' | 'INACTIVE';
  remark?: string;
}

interface LocationFormValues {
  locationCode: string;
  zoneType: LocationZoneType;
  rfidEpc?: string;
  sortNo: number;
  status: 'ACTIVE' | 'INACTIVE';
  remark?: string;
}

/**
 * 机构仓库与科室：新机构落地时先建仓库（采购入库依赖），
 * 科室二级库需挂载科室，科员的数据范围按 `org_warehouse.dept_id` 折算。
 */
export interface OrgStructurePageProps {
  hq: HqApi;
  /** 平台视角（web-system）：多租户筛选；运营端固定本租户。 */
  platform?: boolean;
}

export function OrgStructurePage({ hq, platform = false }: OrgStructurePageProps) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  // 平台视角：先选租户再选机构，避免在跨租户列表里挑错
  const [tenantId, setTenantId] = useState<string | undefined>();
  const [orgId, setOrgId] = useState<string>('');
  const [whOpen, setWhOpen] = useState(false);
  const [whEditing, setWhEditing] = useState<WarehouseRow | null>(null);
  const [deptOpen, setDeptOpen] = useState(false);
  const [deptEditing, setDeptEditing] = useState<DeptRow | null>(null);
  const [locWh, setLocWh] = useState<WarehouseRow | null>(null);
  const [locOpen, setLocOpen] = useState(false);
  const [locEditing, setLocEditing] = useState<LocationRow | null>(null);

  const [whForm] = Form.useForm<WarehouseFormValues>();
  const [deptForm] = Form.useForm<DeptFormValues>();
  const [locForm] = Form.useForm<LocationFormValues>();
  // 外部运营仓（零库存仓/供应商仓）必须登记省市区与地址，院内库可留空
  const whKind = Form.useWatch('whKind', whForm) as WarehouseKind | undefined;

  // 责任供应商：外仓必填，决定供应商门户「可供库存」能看到哪些供货指令
  const suppliers = useQuery({
    queryKey: ['sys', 'supplier', 'options'],
    queryFn: () => hq.catalog.suppliers({ size: 200 }),
    staleTime: 5 * 60 * 1000
  });
  const supplierOptions = (suppliers.data?.items ?? []).map((row) => ({
    value: row.id,
    label: `${row.supplierCode} ${row.supplierName}`
  }));

  const tenants = useQuery({
    queryKey: ['sys', 'tenant', 'options'],
    queryFn: () => hq.tenant.list({}),
    enabled: platform,
    staleTime: 60 * 1000
  });

  const orgs = useQuery({
    queryKey: ['ops', 'org', 'options', tenantId],
    placeholderData: keepPreviousData,
    queryFn: () => hq.org.list(platform ? { tenantId } : undefined),
    staleTime: 60 * 1000
  });

  const warehouses = useQuery({
    queryKey: ['ops', 'warehouse', orgId],
    placeholderData: keepPreviousData,
    queryFn: () => hq.orgStructure.warehouse.list({ orgId }),
    enabled: orgId !== ''
  });

  const depts = useQuery({
    queryKey: ['ops', 'dept', orgId],
    placeholderData: keepPreviousData,
    queryFn: () => hq.orgStructure.dept.list({ orgId }),
    enabled: orgId !== ''
  });

  const locations = useQuery({
    queryKey: ['ops', 'location', locWh?.id],
    placeholderData: keepPreviousData,
    queryFn: () => hq.orgStructure.location.list({ warehouseId: locWh?.id, orgId }),
    enabled: !!locWh
  });

  // 科室主地址：楼宇 → 楼层级联，楼宇变更时清空楼层
  const deptBuildingId = Form.useWatch('buildingId', deptForm);

  const buildings = useQuery({
    queryKey: ['ops', 'building', orgId],
    placeholderData: keepPreviousData,
    queryFn: () => hq.orgStructure.building.list({ orgId }),
    enabled: orgId !== ''
  });

  const floors = useQuery({
    queryKey: ['ops', 'floor', deptBuildingId],
    placeholderData: keepPreviousData,
    queryFn: () => hq.orgStructure.floor.list({ buildingId: deptBuildingId, orgId }),
    enabled: !!deptBuildingId
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['ops', 'warehouse'] });
    await queryClient.invalidateQueries({ queryKey: ['ops', 'dept'] });
  };

  // 科室增删会改变楼宇/楼层的引用计数，同步刷新
  const invalidateDept = async () => {
    await invalidate();
    await queryClient.invalidateQueries({ queryKey: ['ops', 'building'] });
    await queryClient.invalidateQueries({ queryKey: ['ops', 'floor'] });
  };

  const saveWarehouse = useMutation({
    mutationFn: (body: WarehouseSaveRequest) => hq.orgStructure.warehouse.save(body),
    onSuccess: async (result) => {
      message.success(result.updated ? '仓库已更新' : '仓库已新增');
      setWhOpen(false);
      setWhEditing(null);
      whForm.resetFields();
      await invalidate();
    },
    onError: (error) => {
      message.error(error instanceof ApiError ? error.friendlyMessage : '保存失败，请稍后重试');
    }
  });

  const removeWarehouse = useMutation({
    mutationFn: (id: string) => hq.orgStructure.warehouse.remove(id),
    onSuccess: async () => {
      message.success('仓库已删除');
      await invalidate();
    },
    onError: (error) => {
      message.error(error instanceof ApiError ? error.friendlyMessage : '删除失败，请稍后重试');
    }
  });

  const saveDept = useMutation({
    mutationFn: (body: DeptSaveRequest) => hq.orgStructure.dept.save(body),
    onSuccess: async (result) => {
      message.success(result.updated ? '科室已更新' : '科室已新增');
      setDeptOpen(false);
      setDeptEditing(null);
      deptForm.resetFields();
      await invalidateDept();
    },
    onError: (error) => {
      message.error(error instanceof ApiError ? error.friendlyMessage : '保存失败，请稍后重试');
    }
  });

  const removeDept = useMutation({
    mutationFn: (id: string) => hq.orgStructure.dept.remove(id),
    onSuccess: async () => {
      message.success('科室已删除');
      await invalidateDept();
    },
    onError: (error) => {
      message.error(error instanceof ApiError ? error.friendlyMessage : '删除失败，请稍后重试');
    }
  });

  const saveLocation = useMutation({
    mutationFn: (body: LocationSaveRequest) => hq.orgStructure.location.save(body),
    onSuccess: async (result) => {
      message.success(result.updated ? '货位已更新' : '货位已新增');
      setLocOpen(false);
      setLocEditing(null);
      locForm.resetFields();
      await queryClient.invalidateQueries({ queryKey: ['ops', 'location'] });
    },
    onError: (error) => {
      message.error(error instanceof ApiError ? error.friendlyMessage : '保存失败，请稍后重试');
    }
  });

  const removeLocation = useMutation({
    mutationFn: (id: string) => hq.orgStructure.location.remove(id),
    onSuccess: async () => {
      message.success('货位已删除');
      await queryClient.invalidateQueries({ queryKey: ['ops', 'location'] });
    },
    onError: (error) => {
      message.error(error instanceof ApiError ? error.friendlyMessage : '删除失败，请稍后重试');
    }
  });

  const openLocCreate = () => {
    setLocEditing(null);
    locForm.setFieldsValue({ zoneType: 'STORAGE', sortNo: 0, status: 'ACTIVE' });
    setLocOpen(true);
  };

  const openLocEdit = (row: LocationRow) => {
    setLocEditing(row);
    locForm.setFieldsValue({
      locationCode: row.locationCode,
      zoneType: row.zoneType,
      rfidEpc: row.rfidEpc ?? undefined,
      sortNo: row.sortNo,
      status: row.status,
      remark: row.remark ?? undefined
    });
    setLocOpen(true);
  };

  const orgOptions = (orgs.data ?? []).map((row: OrgRow) => ({
    value: row.id,
    label: `${row.orgName}（${row.orgShortName ?? '-'}）`
  }));

  const deptOptions = (depts.data ?? [])
    .filter((row) => row.spaceNodeType === 'DEPT')
    .map((row) => ({ value: row.id, label: row.deptName }));

  const openWhCreate = () => {
    setWhEditing(null);
    whForm.setFieldsValue({ whKind: 'INNER_MAIN', whNature: 'PHYSICAL', allowNegative: false, status: 'ACTIVE' });
    setWhOpen(true);
  };

  const openWhEdit = (row: WarehouseRow) => {
    setWhEditing(row);
    whForm.setFieldsValue({
      whCode: row.whCode,
      whName: row.whName,
      whKind: row.whKind,
      whNature: row.whNature,
      deptId: row.deptId === '0' ? undefined : row.deptId,
      supplierId: row.supplierId === '0' ? undefined : row.supplierId,
      // 级联显示值由 RegionAddressFields 按 districtCode 自动补全
      regionCodes: undefined,
      provinceCode: row.provinceCode ?? undefined,
      cityCode: row.cityCode ?? undefined,
      districtCode: row.districtCode ?? undefined,
      address: row.address ?? undefined,
      lng: row.lng,
      lat: row.lat,
      contactPhone: row.contactPhone ?? undefined,
      allowNegative: row.allowNegative,
      status: row.status,
      remark: row.remark ?? undefined
    });
    setWhOpen(true);
  };

  const openDeptCreate = () => {
    setDeptEditing(null);
    deptForm.setFieldsValue({
      deptType: 'CLINICAL',
      spaceNodeType: 'DEPT',
      acceptDelivery: true,
      canCostBear: true,
      isSubStock: false,
      allowSelfApply: true,
      staffCount: 0,
      bedCount: 0,
      status: 'ACTIVE'
    });
    setDeptOpen(true);
  };

  const openDeptEdit = (row: DeptRow) => {
    setDeptEditing(row);
    deptForm.setFieldsValue({
      deptCode: row.deptCode,
      deptName: row.deptName,
      deptType: row.deptType,
      spaceNodeType: row.spaceNodeType,
      parentId: row.parentId === '0' ? undefined : row.parentId,
      acceptDelivery: row.acceptDelivery,
      canCostBear: row.canCostBear,
      isSubStock: row.isSubStock,
      allowSelfApply: row.allowSelfApply,
      buildingId: row.buildingId === '0' ? undefined : row.buildingId,
      floorId: row.floorId === '0' ? undefined : row.floorId,
      staffCount: row.staffCount,
      bedCount: row.bedCount,
      status: row.status,
      remark: row.remark ?? undefined
    });
    setDeptOpen(true);
  };

  const whColumns: ColumnsType<WarehouseRow> = [
    {
      title: '仓库',
      // 单行展示：编码 / 挂载科室收进悬浮提示，避免行高被拉高
      render: (_, row) => (
        <Tooltip placement="topLeft" title={[row.whCode, row.deptName ? `挂载 ${row.deptName}` : null].filter(Boolean).join(' · ')}>
          <Space size={6} align="center">
            <span>{row.whName}</span>
            <Tag color={WH_KIND_META[row.whKind]?.color ?? 'default'} style={{ marginInlineEnd: 0 }}>
              {WH_KIND_META[row.whKind]?.text ?? row.whKind}
            </Tag>
          </Space>
        </Tooltip>
      )
    },
    {
      title: '性质',
      dataIndex: 'whNature',
      width: 80,
      render: (value: WarehouseNature) => (value === 'VIRTUAL' ? '虚拟' : '实体')
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 90,
      render: (value: string) => (value === 'LOCKED' ? <Tag color="orange">锁定</Tag> : <Tag color="green">正常</Tag>)
    },
    {
      title: '操作',
      width: 160,
      render: (_, row) => (
        <Space size={0}>
          <Button type="link" size="small" onClick={() => setLocWh(row)}>
            货位
          </Button>
          <Button type="link" size="small" onClick={() => openWhEdit(row)}>
            编辑
          </Button>
          <Popconfirm
            title="删除该仓库？"
            description="有货位时后端会拒绝删除"
            okText="删除"
            cancelText="取消"
            onConfirm={() => removeWarehouse.mutate(row.id)}
          >
            <Button type="link" size="small" danger loading={removeWarehouse.isPending}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const deptColumns: ColumnsType<DeptRow> = [
    {
      title: '科室 / 地标',
      // 单行展示：编码 / 上级收进悬浮提示
      render: (_, row) => (
        <Tooltip placement="topLeft" title={[row.deptCode, row.parentName ? `上级 ${row.parentName}` : null].filter(Boolean).join(' · ')}>
          <Space size={6} align="center">
            <span>{row.deptName}</span>
            {row.spaceNodeType === 'LANDMARK' ? <Tag color="geekblue" style={{ marginInlineEnd: 0 }}>地标</Tag> : null}
            {row.isSubStock ? <Tag color="cyan" style={{ marginInlineEnd: 0 }}>二级库</Tag> : null}
          </Space>
        </Tooltip>
      )
    },
    {
      title: '类型',
      dataIndex: 'deptType',
      width: 110,
      render: (value: DeptType) => DEPT_TYPE_META[value] ?? value
    },
    {
      title: '主地址',
      width: 170,
      render: (_, row) =>
        row.buildingName || row.floorName ? (
          <Typography.Text type="secondary">
            {row.buildingName ?? '-'}
            {row.floorName ? ` / ${row.floorName}` : ''}
          </Typography.Text>
        ) : (
          <Typography.Text type="secondary">未定位</Typography.Text>
        )
    },
    {
      title: '接收配送',
      dataIndex: 'acceptDelivery',
      width: 100,
      render: (value: boolean) => (value ? <Tag color="green">是</Tag> : <Tag>否</Tag>)
    },
    {
      title: '操作',
      width: 120,
      render: (_, row) => (
        <Space size={0}>
          <Button type="link" size="small" onClick={() => openDeptEdit(row)}>
            编辑
          </Button>
          <Popconfirm
            title="删除该科室？"
            description={row.childCount > 0 ? `还有 ${row.childCount} 个下级，需先处理` : '被二级库挂载时后端会拒绝'}
            okText="删除"
            cancelText="取消"
            onConfirm={() => removeDept.mutate(row.id)}
          >
            <Button type="link" size="small" danger loading={removeDept.isPending}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <Card
      className="page-card"
      title={
        <Space>
          <ClusterOutlined />
          仓库与科室
        </Space>
      }
      extra={
        <Button
          icon={<ReloadOutlined />}
          loading={warehouses.isFetching || depts.isFetching}
          onClick={() => {
            void warehouses.refetch();
            void depts.refetch();
          }}
        >
          刷新
        </Button>
      }
    >
      <Space style={{ marginBottom: 12 }} wrap>
        {platform ? (
          <>
            <Typography.Text type="secondary">租户</Typography.Text>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              style={{ width: 220 }}
              placeholder="全部租户"
              value={tenantId}
              onChange={(value) => {
                setTenantId(value ?? undefined);
                setOrgId('');
              }}
              options={(tenants.data ?? []).map((item) => ({
                value: String(item.id),
                label: `${item.tenantName}（${item.tenantCode}）`
              }))}
            />
          </>
        ) : null}
        <Typography.Text type="secondary">机构</Typography.Text>
        <Select
          showSearch
          optionFilterProp="label"
          style={{ width: 360 }}
          placeholder="请选择机构"
          value={orgId || undefined}
          onChange={setOrgId}
          options={orgOptions}
        />
      </Space>

      {orgId === '' ? (
        <Alert
          type="info"
          showIcon
          message="先选择机构"
          description="新机构必须先建仓库才能采购入库；科室二级库要挂载科室，科员的数据范围按所挂仓库折算。"
        />
      ) : (
        <Row gutter={16}>
          <Col xs={24} xl={12}>
            <Card
              size="small"
              title="仓库"
              extra={
                <Button icon={<PlusOutlined />} size="small" type="primary" onClick={openWhCreate}>
                  新建仓库
                </Button>
              }
            >
              <Table<WarehouseRow>
                rowKey="id"
                size="small"
                columns={whColumns}
                dataSource={warehouses.data ?? []}
                loading={orgId !== '' && warehouses.isPending}
                pagination={false}
                locale={{ emptyText: '暂无仓库' }}
              />
            </Card>
          </Col>
          <Col xs={24} xl={12}>
            <Card
              size="small"
              title="科室 / 地标"
              style={{ marginTop: 16 }}
              extra={
                <Button icon={<PlusOutlined />} size="small" type="primary" onClick={openDeptCreate}>
                  新建科室
                </Button>
              }
            >
              <Table<DeptRow>
                rowKey="id"
                size="small"
                columns={deptColumns}
                dataSource={depts.data ?? []}
                loading={orgId !== '' && depts.isPending}
                pagination={false}
                locale={{ emptyText: '暂无科室' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      <Modal
        title={whEditing ? '编辑仓库' : '新建仓库'}
        open={whOpen}
        onCancel={() => {
          setWhOpen(false);
          setWhEditing(null);
        }}
        onOk={() => whForm.submit()}
        confirmLoading={saveWarehouse.isPending}
        width={620}
        destroyOnHidden
      >
        <Form<WarehouseFormValues>
          form={whForm}
          layout="vertical"
          onFinish={(values) =>
            saveWarehouse.mutate({
              id: whEditing?.id,
              orgId,
              whCode: values.whCode,
              whName: values.whName,
              whKind: values.whKind,
              whNature: values.whNature,
              deptId: values.deptId ?? '0',
              supplierId: values.supplierId ?? '0',
              provinceCode: values.provinceCode,
              cityCode: values.cityCode,
              districtCode: values.districtCode,
              address: values.address,
              lng: values.lng ?? null,
              lat: values.lat ?? null,
              contactPhone: values.contactPhone,
              allowNegative: values.allowNegative,
              status: values.status,
              remark: values.remark
            })
          }
        >
          <Space size={16} wrap style={{ width: '100%' }} align="start">
            <Form.Item
              name="whCode"
              label="仓库编码"
              rules={[
                { required: true, message: '请填写仓库编码' },
                { pattern: /^[0-9A-Za-z_-]{2,32}$/, message: '字母、数字、下划线或短横线，2–32 位' }
              ]}
              style={{ width: 220 }}
            >
              <Input maxLength={32} placeholder="如 WH-MAIN" />
            </Form.Item>
            <Form.Item name="whName" label="仓库名称" rules={[{ required: true, message: '请填写仓库名称' }]} style={{ width: 340 }}>
              <Input maxLength={64} placeholder="如 院内中心库" />
            </Form.Item>
          </Space>

          <Space size={16} wrap style={{ width: '100%' }} align="start">
            <Form.Item name="whKind" label="仓库类型" rules={[{ required: true, message: '请选择类型' }]} style={{ width: 200 }}>
              <Select
                options={[
                  { value: 'INNER_MAIN', label: '院内中心库' },
                  { value: 'INNER_SUB', label: '科室二级库' },
                  { value: 'OUTER_ZERO', label: '外部零库存仓' },
                  { value: 'SUPPLIER', label: '供应商库' }
                ]}
              />
            </Form.Item>
            <Form.Item name="whNature" label="性质" style={{ width: 140 }}>
              <Select
                options={[
                  { value: 'PHYSICAL', label: '实体' },
                  { value: 'VIRTUAL', label: '虚拟' }
                ]}
              />
            </Form.Item>
            <Form.Item
              noStyle
              shouldUpdate={(prev, next) => prev.whKind !== next.whKind || prev.whNature !== next.whNature}
            >
              {({ getFieldValue }) =>
                getFieldValue('whKind') === 'INNER_SUB' ? (
                  <Form.Item
                    name="deptId"
                    label="挂载科室"
                    rules={[{ required: true, message: '科室二级库必须挂载科室' }]}
                    style={{ width: 220 }}
                  >
                    <Select showSearch optionFilterProp="label" placeholder="选择科室" options={deptOptions} />
                  </Form.Item>
                ) : null
              }
            </Form.Item>
            <Form.Item
              noStyle
              shouldUpdate={(prev, next) => prev.whKind !== next.whKind}
            >
              {({ getFieldValue }) =>
                getFieldValue('whKind') === 'OUTER_ZERO' || getFieldValue('whKind') === 'SUPPLIER' ? (
                  <Form.Item
                    name="supplierId"
                    label="责任供应商"
                    rules={[{ required: true, message: '外部仓必须绑定责任供应商' }]}
                    style={{ width: 300 }}
                  >
                    <Select
                      showSearch
                      optionFilterProp="label"
                      placeholder="承担该仓履约的供应商"
                      options={supplierOptions}
                    />
                  </Form.Item>
                ) : null
              }
            </Form.Item>
            <Form.Item name="status" label="状态" style={{ width: 140 }}>
              <Select
                options={[
                  { value: 'ACTIVE', label: '正常' },
                  { value: 'LOCKED', label: '锁定' }
                ]}
              />
            </Form.Item>
          </Space>

          <RegionAddressFields
            hq={hq}
            form={whForm}
            required={whKind === 'OUTER_ZERO' || whKind === 'SUPPLIER'}
            addressHint="外部运营仓（零库存仓/供应商仓）必填省市区与地址；院内库可留空，沿用机构地址"
          />

          <Form.Item
            name="contactPhone"
            label="联系电话"
            style={{ width: 180 }}
            rules={[
              {
                pattern: /^(1[3-9]\d{9}|0\d{2,3}-?\d{7,8}(-\d{1,6})?)$/,
                message: '手机号或含区号座机（如 0571-88888888）'
              }
            ]}
          >
            <Input maxLength={20} />
          </Form.Item>

          <Form.Item name="allowNegative" label="允许负库存" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={2} maxLength={200} showCount />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={deptEditing ? '编辑科室 / 地标' : '新建科室 / 地标'}
        open={deptOpen}
        onCancel={() => {
          setDeptOpen(false);
          setDeptEditing(null);
        }}
        onOk={() => deptForm.submit()}
        confirmLoading={saveDept.isPending}
        width={680}
        destroyOnHidden
      >
        <Form<DeptFormValues>
          form={deptForm}
          layout="vertical"
          onFinish={(values) =>
            saveDept.mutate({
              id: deptEditing?.id,
              orgId,
              deptCode: values.deptCode,
              deptName: values.deptName,
              parentId: values.parentId ?? '0',
              deptType: values.deptType,
              spaceNodeType: values.spaceNodeType,
              acceptDelivery: values.acceptDelivery,
              canCostBear: values.canCostBear,
              isSubStock: values.isSubStock,
              allowSelfApply: values.allowSelfApply,
              buildingId: values.buildingId,
              floorId: values.floorId,
              staffCount: values.staffCount,
              bedCount: values.bedCount,
              status: values.status,
              remark: values.remark
            })
          }
        >
          <Space size={16} wrap style={{ width: '100%' }} align="start">
            <Form.Item
              name="deptCode"
              label="编码"
              rules={[
                { required: true, message: '请填写编码' },
                { pattern: /^[0-9A-Za-z_-]{2,32}$/, message: '字母、数字、下划线或短横线，2–32 位' }
              ]}
              style={{ width: 200 }}
            >
              <Input maxLength={32} placeholder="如 D-NEURO" />
            </Form.Item>
            <Form.Item name="deptName" label="名称" rules={[{ required: true, message: '请填写名称' }]} style={{ width: 320 }}>
              <Input maxLength={64} placeholder="如 神经内科" />
            </Form.Item>
            <Form.Item name="spaceNodeType" label="空间类型" style={{ width: 150 }}>
              <Select
                options={[
                  { value: 'DEPT', label: '科室' },
                  { value: 'LANDMARK', label: '地标' }
                ]}
              />
            </Form.Item>
          </Space>

          <Space size={16} wrap style={{ width: '100%' }} align="start">
            <Form.Item name="deptType" label="科室类型" rules={[{ required: true, message: '请选择类型' }]} style={{ width: 180 }}>
              <Select
                options={[
                  { value: 'CLINICAL', label: '临床科室' },
                  { value: 'ADMIN', label: '行政科室' },
                  { value: 'LOGISTICS', label: '后勤' },
                  { value: 'NURSING_UNIT', label: '护理单元' },
                  { value: 'OPD_EMERG', label: '门急诊' },
                  { value: 'LANDMARK', label: '地标' }
                ]}
              />
            </Form.Item>
            <Form.Item name="parentId" label="上级科室" style={{ width: 220 }}>
              <Select allowClear showSearch optionFilterProp="label" placeholder="可留空" options={deptOptions} />
            </Form.Item>
            <Form.Item name="status" label="状态" style={{ width: 140 }}>
              <Select
                options={[
                  { value: 'ACTIVE', label: '启用' },
                  { value: 'INACTIVE', label: '停用' }
                ]}
              />
            </Form.Item>
          </Space>

          <Space size={16} wrap style={{ width: '100%' }} align="start">
            <Form.Item name="buildingId" label="楼宇" style={{ width: 240 }}>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="可留空"
                loading={buildings.isFetching}
                onChange={() => deptForm.setFieldValue('floorId', undefined)}
                options={(buildings.data ?? []).map((row) => ({
                  value: row.id,
                  label: row.campusName ? `${row.buildingName}（${row.campusName}）` : row.buildingName
                }))}
              />
            </Form.Item>
            <Form.Item name="floorId" label="楼层" style={{ width: 200 }}>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder={deptBuildingId ? '可留空' : '先选楼宇'}
                disabled={!deptBuildingId}
                loading={floors.isFetching}
                options={(floors.data ?? []).map((row) => ({ value: row.id, label: row.floorName }))}
              />
            </Form.Item>
            <Form.Item label=" " colon={false}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                分楼层配送与室内导航按此地址
              </Typography.Text>
            </Form.Item>
          </Space>

          <Space size={16} wrap style={{ width: '100%' }} align="start">
            <Form.Item name="staffCount" label="人数" style={{ width: 120 }}>
              <InputNumber min={0} max={100000} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="bedCount" label="床位数" style={{ width: 120 }}>
              <InputNumber min={0} max={100000} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="acceptDelivery" label="接收配送" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item name="isSubStock" label="二级库" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item name="canCostBear" label="成本对象" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item name="allowSelfApply" label="允许自主申领" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Space>

          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={2} maxLength={200} showCount />
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title={`货位 · ${locWh?.whName ?? ''}`}
        open={locWh !== null}
        width={720}
        onClose={() => setLocWh(null)}
        extra={
          <Button icon={<PlusOutlined />} type="primary" size="small" onClick={openLocCreate}>
            新建货位
          </Button>
        }
      >
        <Table<LocationRow>
          rowKey="id"
          size="small"
          loading={!!locWh && locations.isPending}
          dataSource={locations.data ?? []}
          pagination={false}
          locale={{ emptyText: '该仓库暂无货位，入库前建议先建存储货位' }}
          columns={[
            { title: '货位编码', dataIndex: 'locationCode', width: 160 },
            {
              title: '用途',
              dataIndex: 'zoneType',
              width: 110,
              render: (value: LocationZoneType) => {
                const meta = ZONE_META[value] ?? { text: value, color: 'default' };
                return <Tag color={meta.color}>{meta.text}</Tag>;
              }
            },
            { title: '排序', dataIndex: 'sortNo', width: 80, align: 'right' },
            { title: 'RFID EPC', dataIndex: 'rfidEpc', render: (value: string | null) => value || '-' },
            {
              title: '状态',
              dataIndex: 'status',
              width: 90,
              render: (value: string) => (value === 'ACTIVE' ? <Tag color="green">启用</Tag> : <Tag>停用</Tag>)
            },
            {
              title: '操作',
              width: 120,
              render: (_, row) => (
                <Space size={0}>
                  <Button type="link" size="small" onClick={() => openLocEdit(row)}>
                    编辑
                  </Button>
                  <Popconfirm
                    title="删除该货位？"
                    description="货位上有库存时后端会拒绝删除"
                    okText="删除"
                    cancelText="取消"
                    onConfirm={() => removeLocation.mutate(row.id)}
                  >
                    <Button type="link" size="small" danger loading={removeLocation.isPending}>
                      删除
                    </Button>
                  </Popconfirm>
                </Space>
              )
            }
          ]}
        />
      </Drawer>

      <Modal
        title={locEditing ? '编辑货位' : '新建货位'}
        open={locOpen}
        onCancel={() => {
          setLocOpen(false);
          setLocEditing(null);
        }}
        onOk={() => locForm.submit()}
        confirmLoading={saveLocation.isPending}
        width={520}
        destroyOnHidden
      >
        <Form<LocationFormValues>
          form={locForm}
          layout="vertical"
          onFinish={(values) =>
            saveLocation.mutate({
              id: locEditing?.id,
              orgId,
              warehouseId: locWh?.id,
              locationCode: values.locationCode,
              zoneType: values.zoneType,
              rfidEpc: values.rfidEpc,
              sortNo: values.sortNo,
              status: values.status,
              remark: values.remark
            })
          }
        >
          <Space size={16} wrap style={{ width: '100%' }} align="start">
            <Form.Item
              name="locationCode"
              label="货位编码"
              rules={[
                { required: true, message: '请填写货位编码' },
                { pattern: /^[0-9A-Za-z_-]{2,32}$/, message: '字母、数字、下划线或短横线，2–32 位' }
              ]}
              style={{ width: 220 }}
            >
              <Input maxLength={32} placeholder="如 A-01-02" />
            </Form.Item>
            <Form.Item name="zoneType" label="用途" style={{ width: 160 }}>
              <Select
                options={[
                  { value: 'STORAGE', label: '存储' },
                  { value: 'PICK', label: '拣选' },
                  { value: 'RECEIVE', label: '收货' },
                  { value: 'RETURN', label: '退货暂存' },
                  { value: 'SCRAP', label: '报废区' }
                ]}
              />
            </Form.Item>
            <Form.Item name="sortNo" label="排序" style={{ width: 100 }}>
              <InputNumber min={0} max={99999} style={{ width: '100%' }} />
            </Form.Item>
          </Space>

          <Space size={16} wrap style={{ width: '100%' }} align="start">
            <Form.Item name="rfidEpc" label="RFID EPC" style={{ width: 280 }}>
              <Input maxLength={64} placeholder="选填" />
            </Form.Item>
            <Form.Item name="status" label="状态" style={{ width: 140 }}>
              <Select
                options={[
                  { value: 'ACTIVE', label: '启用' },
                  { value: 'INACTIVE', label: '停用' }
                ]}
              />
            </Form.Item>
          </Space>

          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={2} maxLength={200} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
